/**
 * What a MAX button resolves to.
 *
 * A maximum is either known, or it is not — and "not known" is never the same as
 * zero. A balance still loading, an unpriced position, a fee schedule the
 * backend has not sent: each of those used to fill the field with 0 and leave
 * the trader guessing. `MaxResult` carries the reason instead, so MAX can be
 * disabled and say why.
 */
import { Dec } from "../decimal";

/** What actually held the maximum down — shown as "max: your balance" etc. */
export type MaxLimiter =
  | "balance"
  | "buying_power"
  | "position"
  | "max_order_size"
  | "withdrawable"
  | "gas_reserve"
  | "initial_margin";

/** Why no maximum can be offered right now. */
export type MaxUnavailableReason =
  | "not_connected"
  | "loading"
  | "balance_unknown"
  | "price_unknown"
  | "fee_unknown"
  | "leverage_unknown"
  | "specs_unknown"
  | "withdrawable_unknown"
  | "zero_balance"
  | "below_min_order"
  | "no_position"
  /** Positions have not loaded (or their read failed): not the same as none. */
  | "positions_unknown";

export type MaxResult =
  | { ok: true; max: Dec; limitedBy: MaxLimiter; unit: string }
  | { ok: false; reason: MaxUnavailableReason; min?: Dec };

export const maxOk = (max: Dec, limitedBy: MaxLimiter, unit: string): MaxResult => ({
  ok: true,
  max,
  limitedBy,
  unit,
});

export const maxUnavailable = (
  reason: MaxUnavailableReason,
  min?: Dec
): MaxResult => ({ ok: false, reason, min });
